// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Mini-Game Launcher Component
 * 
 * Handles rendering of asteroid and combat mini-games
 * Extracted from EnhancedWarpEventManager for better code organization
 *
 * Performance: Both minigames are lazy-loaded — they only enter the bundle
 * the first time the player triggers a warp encounter. Combat (~1,036 LOC)
 * and Asteroid (~711 LOC) are the two largest gameplay components.
 */

import React, { lazy, Suspense } from 'react';
import QuantumStormEffect from './QuantumStormEffect';

const AsteroidMiniGame = lazy(() => import('./AsteroidMiniGame/AsteroidMiniGame'));
const CombatMiniGame = lazy(() => import('./CombatMiniGame/CombatMiniGame'));

const MiniGameLauncher = ({
  showAsteroidMiniGame,
  showCombatMiniGame,
  miniGameConfig,
  weapons,
  showQuantumStorm,
  quantumStormIntensity,
  onAsteroidComplete,
  onAsteroidGameOver,
  onAsteroidClose,
  onCombatVictory,
  onCombatDefeat,
  onCombatClose,
  onQuantumStormComplete
}) => {
  // Render combat mini-game if active
  if (showCombatMiniGame && miniGameConfig) {
    return (
      <>
        <Suspense fallback={null}>
          <CombatMiniGame
            enemies={miniGameConfig.enemies || []}
            playerWeapons={(() => {
              
              const allWeapons = weapons && weapons.length > 0 
                ? weapons.slice(0, 4).map(w => ({
                    id: w.id,
                    name: w.installed ? w.name : 'EMPTY SLOT',
                    damage: w.installed ? w.damage : 0,
                    ammo: w.installed ? (w.ammo || 100) : 0,
                    installed: w.installed
                  }))
                : [{ id: 'laser', name: 'Basic Laser', damage: 20, ammo: 100, installed: true }];
              
              return allWeapons;
            })()}
            onVictory={onCombatVictory}
            onDefeat={onCombatDefeat}
            onClose={onCombatClose}
          />
        </Suspense>
        {/* Quantum Storm Effect - can show during mini-game */}
        <QuantumStormEffect
          isActive={showQuantumStorm}
          intensity={quantumStormIntensity}
          onComplete={onQuantumStormComplete}
        />
      </>
    );
  }

  // Render asteroid mini-game if active
  if (showAsteroidMiniGame && miniGameConfig) {
    return (
      <>
        <Suspense fallback={null}>
          <AsteroidMiniGame
            difficulty={miniGameConfig.difficulty}
            duration={miniGameConfig.duration}
            onComplete={onAsteroidComplete}
            onGameOver={onAsteroidGameOver}
            onClose={onAsteroidClose}
          />
        </Suspense>
        {/* Quantum Storm Effect - can show during mini-game */}
        <QuantumStormEffect
          isActive={showQuantumStorm}
          intensity={quantumStormIntensity}
          onComplete={onQuantumStormComplete}
        />
      </>
    );
  }

  // No mini-game active - just render quantum storm if needed
  return (
    <QuantumStormEffect
      isActive={showQuantumStorm}
      intensity={quantumStormIntensity}
      onComplete={onQuantumStormComplete}
    />
  );
};

export default MiniGameLauncher;
